import { useContext, useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";

import {
  addReservation,
  setOverlayValue,
  setHotelResults,
  setNumPersons,
  setNumRooms,
} from "store";
import visbook from "api/visbook";
import WP from "WP";
import ImageGalleryContext from "context/image-gallery-context";

import Container from "partials/Container";
import GridElement from "partials/GridElement";
import LoadingButton from "partials/LoadingButton";
import Button from "partials/Button";
import Html from "partials/Html";
import ImageGallery from "partials/ImageGallery";
import CancelOrderTerms from "partials/CancelOrderTerms";
import MobileRoomTypeCard from "./MobileRoomTypeCard";
import RoomTypeInputCounter from "./RoomTypeInputCounter";
import IconSelector from "partials/IconSelector";

import Media from "css/Media";

const RoomTypeCard = (props, { hotelName }) => {
  //console.log("[DEBUG] RoomTypeCard props", props);
  //console.log("[DEBUG] RoomTypeCard hotelName", hotelName);
  const { availability, room } = props;
  const [open, setOpen] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [filtered, setFiltered] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const imageGalleryContext = useContext(ImageGalleryContext);

  useEffect(() => {
    checkAvailability();
  }, []);

  useEffect(() => {
    checkAvailability();
  }, [availability]);

  const checkAvailability = () => {
    let isFiltered =
      availability.fromDate &&
      availability.toDate &&
      availability.numRooms > 0 &&
      availability.numPersons > 0
        ? true
        : false;
    let disabled = true;

    availability.hotels.map((roomType) => {
      if (roomType.id == room.visbook_room_id) {
        if (roomType.available) {
          disabled = false;
        }
      }
      return null;
    });
    setFiltered(isFiltered);
    setDisabled(disabled);
  };

  const handleInfoClick = () => {
    setOpen(!open);
  };

  const handleTermsClick = () => {
    props.setOverlayValue({
      visible: true,
      component: <CancelOrderTerms />,
    });
    imageGalleryContext.setOpen(true);
  };

  const onGalleryClick = (images) => {
    props.setOverlayValue({
      visible: true,
      component: <ImageGallery images={images} startAt={0} />,
    });
    imageGalleryContext.setOpen(true);
  };

  const getPrice = (room) => {
    if (room.availability.available) {
      let calculatedPrice = room.prices[0].calculatedPrice;
      let numRooms = availability.numRooms;
      let price = calculatedPrice * numRooms;

      return "NOK " + price.toLocaleString("fr");
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    //console.log("[DEBUG] Starting reservation process");
    //console.log("[DEBUG] Availability:", availability);
    //console.log("[DEBUG] Room:", room);

    try {
      const reservationPromises = Array.from(
        { length: availability.numRooms },
        (_, index) => {
          //console.log(`[DEBUG] Attempting to reserve room ${index + 1}`);
          return visbook.reserveRoom({
            webProductId: room.availability.id,
            fromDate: availability.fromDate,
            toDate: availability.toDate,
            priceId: room.availability.prices[0].id,
            numberOfPeople: parseInt(availability.numPersons),
          });
        }
      );

      const results = await Promise.allSettled(reservationPromises);
      //console.log("[DEBUG] Reservation results:", results);

      const successfulReservations = results
        .filter((result) => result.status === "fulfilled")
        .map((result) => result.value);

      //console.log("[DEBUG] Successful reservations:", successfulReservations);

      if (successfulReservations.length > 0) {
        successfulReservations.forEach((r, index) => {
          //console.log(
          //   `[DEBUG] Processing successful reservation ${index + 1}:`,
          //   r
          // );
          props.addReservation({
            reservationId: r.data[0].reservationId,
            encryptedCompanyId: r.data[0].encryptedCompanyId,
            reservationTime: new Date(),
            hotelName: hotelName,
            room: room,
          });
        });

        //console.log("[DEBUG] Navigating to checkout");
        props.history.push("/checkout");
      } else {
        console.error("[ERROR] No reservations were successful");
        alert(
          "We're sorry, but no rooms could be reserved at this time. Please try again later."
        );
      }
    } catch (error) {
      // console.error("[ERROR] Error during reservation process:", error);
      // if (error.response) {
      //   console.error("[ERROR] Response data:", error.response.data);
      //   console.error("[ERROR] Response status:", error.response.status);
      //   console.error("[ERROR] Response headers:", error.response.headers);
      // } else if (error.request) {
      //   console.error("[ERROR] No response received:", error.request);
      // } else {
      //   console.error("[ERROR] Error message:", error.message);
      // }
      alert(
        "An error occurred while trying to make your reservation. Please try again later."
      );
    } finally {
      //console.log("[DEBUG] Reservation process completed");
      setSubmitting(false);
    }
  };

  const handleVisbookUpdate = (name, value) => {
    if (name === "numRooms") {
      props.setNumRooms(value);
    } else if (name === "numPersons") {
      props.setNumPersons(value);
    }
    handleVisbookSearch();
  };

  const handleVisbookSearch = () => {
    visbook
      .webproducts({
        fromDate: availability.fromDate,
        toDate: availability.toDate,
        numRooms: availability.numRooms,
        numPersons: availability.numPersons,
      })
      .then((resp) => props.setHotelResults(resp));
  };
  if (!filtered) return null;

  return (
    <>
      <Wrapper>
        <Container grid>
          <GridElement colStart={3} colEnd={22}>
            <InnerGrid>
              {room.featured_image && (
                <GridElement colStart={1} colEnd={9}>
                  <ImageOverlay
                    disabled={disabled}
                    onClick={
                      disabled ? null : () => onGalleryClick(room.image_gallery)
                    }
                  >
                    <Image image={room.featured_image}>
                      <GalleryPopUp>
                        <IconSelector icon="gallery" negative />
                        <span>{`+ ${room.image_gallery.length}`}</span>
                      </GalleryPopUp>
                    </Image>
                  </ImageOverlay>
                </GridElement>
              )}
              <ContentGridElement colStart={9} colEnd={20} disabled={disabled}>
                <div>
                  <h3>{room.room_name}</h3>
                  <InfoHtml content>{room.rich_text[0].text_field}</InfoHtml>
                  <InfoButton onClick={handleInfoClick}>
                    {open ? WP.trans.lessInfo : WP.trans.moreInfo}
                  </InfoButton>
                </div>
                <ButtonGroup>
                  {!disabled && (
                    <Controls>
                      <RoomsRoomTypeInputCounter
                        name="numRooms"
                        handleChange={(name, value) =>
                          handleVisbookUpdate(name, value)
                        }
                        value={availability.numRooms}
                        unit={WP.trans.rooms}
                        green
                      />
                      <PersonsRoomTypeInputCounter
                        name="numPersons"
                        handleChange={(name, value) =>
                          handleVisbookUpdate(name, value)
                        }
                        value={availability.numPersons}
                        unit={WP.trans.guestsPerRoom}
                        green
                      />
                    </Controls>
                  )}
                  <ButtonWrapper>
                    {!disabled && (
                      <PriceInfo>
                        <p>
                          {availability.hotels.map((roomType) => {
                            if (roomType.id == room.visbook_room_id) {
                              return getPrice(roomType);
                            } else return null;
                          })}
                        </p>
                      </PriceInfo>
                    )}
                    {submitting ? (
                      <StyledLoadingButton placeholder={WP.trans.order} />
                    ) : (
                      <StyledButton
                        disabled={disabled}
                        onClick={disabled ? null : handleSubmit}
                      >
                        {disabled ? WP.trans.noAvailableRooms : WP.trans.order}
                      </StyledButton>
                    )}
                  </ButtonWrapper>
                </ButtonGroup>
              </ContentGridElement>
            </InnerGrid>
          </GridElement>
        </Container>
        {open && (
          <Container grid>
            <InfoGridElement colStart={3} colEnd={22} disabled={disabled}>
              <Html content right>
                {room.rich_text[1].text_field}
              </Html>
              <TermsPopUp onClick={handleTermsClick}>
                <IconSelector icon="ticket" negative />
                <p>{WP.trans.orderAndTerms}</p>
              </TermsPopUp>
            </InfoGridElement>
          </Container>
        )}
      </Wrapper>
      <MobileRoomTypeCard
        room={room}
        disabled={disabled}
        open={open}
        numRooms={availability.numRooms}
        numPersons={availability.numPersons}
        onGalleryClick={(gallery) => onGalleryClick(gallery)}
        handleInfoClick={handleInfoClick}
        handleTermsClick={handleTermsClick}
        handleChange={(name, value) => handleVisbookUpdate(name, value)}
        handleSubmit={handleSubmit}
        availability={availability}
        getPrice={(room) => getPrice(room)}
        submitting={submitting}
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  availability: state.availability,
  booking: state.booking,
});

const actions = {
  setOverlayValue,
  addReservation,
  setHotelResults,
  setNumPersons,
  setNumRooms,
};

export default connect(mapStateToProps, actions)(withRouter(RoomTypeCard));

const Wrapper = styled.div`
  display: none;
  ${Media.greaterThan("m")`
      display: block;
      user-select: none;
      width: 100%;
      margin-bottom: ${(props) => props.theme.spacing.elementS};
  `}
`;

const InnerGrid = styled.div`
  display: grid;
  grid-template-rows: ${(props) => props.rows || "auto"};
  grid-template-columns: repeat(9, 1fr 0.67fr) 1fr;

  ${Media.greaterThan("m")`
        grid-template-columns: repeat(9, 1fr .435fr) 1fr;
    `}
`;

const Image = styled.div`
  width: 100%;
  height: 100%;
  background-position: center;
  background-image: url(${(props) => props.image.sizes.full.url});
  background-repeat: no-repeat;
  background-size: cover;

  span {
    ${(props) => props.theme.fonts.p}
  }
`;

const ImageOverlay = styled.div`
  width: 100%;
  height: 100%;

  &::after {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    content: "";
    background-color: transparent;
    transition: all 0.2s ease-in;
  }
  ${(props) =>
    !props.disabled &&
    css`
      cursor: pointer;
      &:hover {
        &::after {
          background-color: rgba(0, 0, 0, 0.2);
        }
      }
    `}
`;

const GalleryPopUp = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  padding: ${(props) => props.theme.spacing.contentL} 25px;
  z-index: 1;
  display: flex;
  color: ${(props) => props.theme.colors.negative};

  span {
    margin-right: ${(props) => props.theme.spacing.contentXS};
  }

  p {
    text-decoration: underline;
  }

  ${Media.greaterThan("l")`
        padding: ${(props) => props.theme.spacing.contentL} ${(props) =>
    props.theme.spacing.contentXL};
    `}
`;

const ContentGridElement = styled(GridElement)`
  background-color: ${(props) =>
    props.disabled ? props.theme.colors.disabled : props.theme.colors.bg};
  color: ${(props) => props.theme.colors.text};
  padding: ${(props) =>
    `${props.theme.spacing.contentL} ${props.theme.spacing.contentXL}`};
  h3 {
    margin-bottom: ${(props) => props.theme.spacing.contentL};
  }

  ${Media.greaterThan("l")`
        padding: ${(props) =>
          `${props.theme.spacing.elementXS} ${props.theme.spacing.elementXS} ${props.theme.spacing.contentXL} ${props.theme.spacing.elementS}`};
    `}
`;

const InfoButton = styled.button`
  ${(props) => props.theme.fonts.p}
  text-decoration: underline;
  cursor: pointer;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Controls = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.5fr) 12px minmax(0, 1fr);
  align-items: end;
  width: 100%;
  margin-right: 6%;
`;

const RoomsRoomTypeInputCounter = styled(RoomTypeInputCounter)`
  grid-column: 1 / 2;
`;

const PersonsRoomTypeInputCounter = styled(RoomTypeInputCounter)`
  grid-column: 3 / 4;
`;

const ButtonWrapper = styled.div`
  text-align: center;
  margin: auto 0 auto auto;
`;

const PriceInfo = styled.div`
  margin-bottom: ${(props) => props.theme.spacing.contentS};
  p {
    font-weight: 600;
  }

  ${Media.greaterThan("l")`
        margin-bottom: ${(props) => props.theme.spacing.contentM};
    `}
`;

const StyledButton = styled(Button)`
  padding: ${(props) => `${props.theme.spacing.contentS}
    ${props.theme.spacing.contentXL}`};

  ${Media.greaterThan("l")`
        padding: ${(props) =>
          `${props.theme.spacing.contentL} ${props.theme.spacing.elementL}`};
    `}
`;

const StyledLoadingButton = styled(LoadingButton)`
  padding: ${(props) => `${props.theme.spacing.contentS}
    ${props.theme.spacing.contentXL}`};

  ${Media.greaterThan("l")`
        padding: ${(props) =>
          `${props.theme.spacing.contentL} ${props.theme.spacing.elementL}`};
    `}
`;

const InfoGridElement = styled(GridElement)`
  padding: ${(props) => props.theme.spacing.contentXL} 65px;
  background-color: ${(props) =>
    props.disabled ? props.theme.colors.disabled : props.theme.colors.bgDark};
`;

const InfoHtml = styled(Html)`
  width: 90%;
  margin-bottom: ${(props) => props.theme.spacing.contentL};

  ${Media.greaterThan("l")`
        width: 100%:
        margin-bottom: ${(props) => props.theme.spacing.contentXL};
    `}
`;

const TermsPopUp = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;

  span {
    margin-right: ${(props) => props.theme.spacing.contentXS};
  }

  p {
    text-decoration: underline;
  }
`;
